import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import {
    StyledTableCell,
    TableCellHeaderContent,
    TableContainer,
    StyledTable,
} from '../../../../shared/components/CustomTable/Styled';
import { FilesTableColumns } from '../../../../configs/files';
import { FileRow } from '../FileTable';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { StyledNavLink } from '../../../../shared/styled/TableStyles';
import { File } from '../../../../data/entities';

interface GroupedFileTableProps {
    itemList: File[];
    extraRowProps: any;
}

interface FileGroup {
    groupKey: string;
    groupName: string;
    groupType: string;
    linkPath: string;
    files: File[];
}

const GroupedFileTable = ({ itemList, extraRowProps }: GroupedFileTableProps) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Group files by linked object
    const groupedFiles = useMemo(() => {
        const groups = new Map<string, FileGroup>();

        itemList.forEach((file) => {
            let groupKey = 'unlinked';
            let groupName = 'Chưa liên kết';
            let groupType = '';
            let linkPath = '';

            if (file.links && file.links.length > 0) {
                const link = file.links[0];
                const objectId = link.object_id;
                const contentType = link.content_type.toLowerCase();
                
                // Use meta for display name if available, otherwise use object_id
                groupName = link.meta?.object_name || objectId;
                groupKey = `${contentType}_${objectId}`;
                
                // Map content_type to route path
                if (contentType === 'product_flow.saleorder') {
                    groupType = 'Đơn hàng sản xuất';
                    linkPath = `/product-orders/${objectId}`;
                } else if (contentType === 'materials.materialorder') {
                    groupType = 'Đơn hàng vật liệu';
                    linkPath = `/material-orders/${objectId}`;
                } else if (contentType === 'products.productorder') {
                    groupType = 'Đơn hàng sản phẩm';
                    linkPath = `/product-orders/${objectId}`;
                } else if (contentType === 'products.product') {
                    groupType = 'Sản phẩm';
                    linkPath = `/products/${objectId}`;
                } else if (contentType === 'materials.material') {
                    groupType = 'Vật liệu';
                    linkPath = `/materials/${objectId}`;
                } else if (contentType === 'products.customer') {
                    groupType = 'Khách hàng';
                    linkPath = `/customers/${objectId}`;
                } else {
                    groupType = 'Khác';
                }
            }

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    groupKey,
                    groupName,
                    groupType,
                    linkPath,
                    files: [],
                });
            }

            groups.get(groupKey)!.files.push(file);
        });

        // Convert to array and sort
        const groupsArray = Array.from(groups.values());

        // Sort groups by the latest upload date first (newest file in group)
        groupsArray.sort((a, b) => {
            // Unlinked files go to the end
            if (a.groupKey === 'unlinked') return 1;
            if (b.groupKey === 'unlinked') return -1;
            
            // Find the latest file in each group
            const getLatestDate = (group: FileGroup) => {
                if (group.files.length === 0) return 0;
                return Math.max(...group.files.map(f => new Date(f.created_at).getTime()));
            };
            
            const dateA = getLatestDate(a);
            const dateB = getLatestDate(b);
            
            // Sort by latest date (newest first)
            return dateB - dateA;
        });

        // Sort files within each group by date (newest first)
        groupsArray.forEach((group) => {
            group.files.sort((a, b) => {
                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateB - dateA; // Newest first
            });
        });

        return groupsArray;
    }, [itemList]);

    // Expand all groups by default
    React.useEffect(() => {
        const allGroupKeys = new Set(groupedFiles.map(g => g.groupKey));
        setExpandedGroups(allGroupKeys);
    }, [groupedFiles.length]);

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(groupKey)) {
                newSet.delete(groupKey);
            } else {
                newSet.add(groupKey);
            }
            return newSet;
        });
    };

    const isGroupExpanded = (groupKey: string) => expandedGroups.has(groupKey);

    return (
        <TableContainer>
            <StyledTable>
                <TableHead>
                    <TableRow>
                        {FilesTableColumns.map((column, index) => (
                            <StyledTableCell key={index}>
                                <TableCellHeaderContent>
                                    {column.text}
                                    {column.icon}
                                </TableCellHeaderContent>
                            </StyledTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupedFiles.map((group) => {
                        const isExpanded = isGroupExpanded(group.groupKey);
                        const fileCount = group.files.length;

                        return (
                            <React.Fragment key={group.groupKey}>
                                {/* Group Header Row */}
                                <TableRow
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#eeeeee',
                                        },
                                    }}
                                    onClick={() => toggleGroup(group.groupKey)}
                                >
                                    <StyledTableCell colSpan={FilesTableColumns.length}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 0',
                                            }}
                                        >
                                            {isExpanded ? (
                                                <ChevronDown size={16} />
                                            ) : (
                                                <ChevronRight size={16} />
                                            )}
                                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                                {group.groupType && `${group.groupType}: `}
                                                {group.linkPath ? (
                                                    <StyledNavLink
                                                        to={group.linkPath}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ fontWeight: 600 }}
                                                    >
                                                        {group.groupName}
                                                    </StyledNavLink>
                                                ) : (
                                                    group.groupName
                                                )}
                                            </span>
                                            <span
                                                style={{
                                                    marginLeft: '8px',
                                                    fontSize: '12px',
                                                    color: '#6B7280',
                                                }}
                                            >
                                                ({fileCount} {fileCount === 1 ? 'tệp' : 'tệp'})
                                            </span>
                                        </div>
                                    </StyledTableCell>
                                </TableRow>

                                {/* Group Files */}
                                {isExpanded &&
                                    group.files.map((file) => (
                                        <FileRow
                                            key={file.id}
                                            item={file}
                                            extraRowProps={extraRowProps}
                                        />
                                    ))}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};

export default GroupedFileTable;

